import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useHistory, Redirect } from 'react-router-dom'
import { CompletePlanAgreementForm } from '../../automatic-rollovers/forms'
import {
  AccountAudience,
  FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
  isGfBDAgreementsEnabled,
} from 'config/portal'
import {
  CompanyType,
  InvestmentType,
  valicFiveInvestmentTypes,
  lincolnInvestmentTypes,
  SigningMethod,
  signingMethodOptions,
  AudienceType,
  GrandfatheredUserValue,
  GFServicesAgreementSubmissionType,
} from 'config/automatic-rollovers'
import { ProductAgreementType } from 'config/products'
import {
  NewAgreementSuccessMessages,
  TOAST_ERROR_CONTACT_SUPPORT,
} from 'config/notifications'
import { ServicesAgreementSubmissionType } from 'config/taxable-accounts'
import { FullPageSpinner, ComponentFailure, Spinner } from 'components'
import { createAudienceObject, getEntityInitialValues } from 'utils'
import * as Types from 'types'
import { compact, isEmpty, find, mergeAll, concat } from 'lodash'

const electronicSignatureOption = signingMethodOptions.find(
  (option) => option.key === SigningMethod.ELECTRONIC_SIGNATURE
)

const ENTERPRISE_RK_SET = new Set([
  'American Funds/Capital Group',
  'Transamerica Retirement Management',
  'Empower',
])

const TAXABLE_INITIAL_INVESTMENT_NAME = 'Federated Govt. Obligations Fund'

const generateSuccessMessage = ({ recordkeeperName, signingMethod }) => {
  if (ENTERPRISE_RK_SET.has(recordkeeperName)) {
    return NewAgreementSuccessMessages.ENTERPRISE_RECORD_KEEPER
  } else if (signingMethod === electronicSignatureOption.value) {
    return NewAgreementSuccessMessages.ELECTRONIC_SIGNATURE
  } else {
    return NewAgreementSuccessMessages.WET_SIGNATURE
  }
}

function findExistingEntity(id, entities) {
  if (!entities) return
  return entities.find((entity) => entity.company.ID === id)
}

function createExistingEntityObject({ id, entities }) {
  const existingEntity = findExistingEntity(id, entities)
  if (!existingEntity) return
  return getEntityInitialValues(existingEntity)
}

function getTaxableSubmissionType(signingMethod) {
  const option = find(signingMethodOptions, ['value', signingMethod])
  if (option?.key === SigningMethod.WET_SIGNATURE) {
    return ServicesAgreementSubmissionType.NEW_AGREEMENT_WET_SIGN
  }
  return ServicesAgreementSubmissionType.NEW_AGREEMENT_ELECTRONIC_OR_ESIGN
}
function getGFServicesAgreementSubmissionType(signingMethod) {
  const option = find(signingMethodOptions, ['value', signingMethod])
  if (option?.key === SigningMethod.WET_SIGNATURE) {
    return GFServicesAgreementSubmissionType.NEW_AGREEMENT_WET_SIGN
  }
  return GFServicesAgreementSubmissionType.NEW_AGREEMENT_ELECTRONIC_OR_ESIGN
}

function getRecordkeeperAgreementType(accountName, findAgreementInfo) {
  const typeMap = {
    [CompanyType.NATIONWIDE]: InvestmentType.NATIONWIDE,
    [CompanyType.TRANSAMERICA]: InvestmentType.TRANSAMERICA,
    [CompanyType.AMERITAS]: InvestmentType.AMERITAS,
    [CompanyType.PENSION]: InvestmentType.VALIC_8,
    [CompanyType.COREBRIDGE]: InvestmentType.VALIC_6,
    [CompanyType.STANDARD]: InvestmentType.STANDARD,
  }
  const mappedType = typeMap[accountName]
  if (mappedType) return findAgreementInfo(mappedType)
  if (valicFiveInvestmentTypes.includes(accountName))
    return findAgreementInfo(InvestmentType.VALIC_5)
  if (lincolnInvestmentTypes.includes(accountName))
    return findAgreementInfo(InvestmentType.AMERITAS)
  return InvestmentType.DEFAULT
}

function computeAllowRecordkeeperSelection(
  isTaxableAccounts,
  audience,
  isPlanFiduciary
) {
  return (
    audience === AccountAudience.TPA ||
    audience === AudienceType.CONSULTANT ||
    isPlanFiduciary
  )
}

function computeIsLoading({
  transformedPlanSponsors,
  allowPlanSponsorSelection,
  transformedRecordkeepers,
  allowRecordkeeperSelection,
  transformedTpas,
  transformedConsultants,
  allowTpaConsultantSelection,
  availableInitialInvestments,
  isTaxableAccounts,
  error,
}) {
  const hasMissingPlanSponsors =
    !transformedPlanSponsors && allowPlanSponsorSelection
  const hasMissingRecordkeepers =
    !transformedRecordkeepers && allowRecordkeeperSelection
  const hasMissingTpasConsultants =
    !transformedTpas && !transformedConsultants && allowTpaConsultantSelection
  const hasMissingInitialInvestments =
    !availableInitialInvestments && !isTaxableAccounts
  return (
    (hasMissingPlanSponsors ||
      hasMissingRecordkeepers ||
      hasMissingTpasConsultants ||
      hasMissingInitialInvestments) &&
    !error
  )
}

const propTypes = {
  flashErrorMessageHandler: PropTypes.func.isRequired,
  fetchPlanSponsors: PropTypes.func.isRequired,
  fetchRecordkeepers: PropTypes.func.isRequired,
  fetchTpas: PropTypes.func.isRequired,
  fetchConsultants: PropTypes.func.isRequired,
  transformedPlanSponsors: PropTypes.arrayOf(Types.audienceDetails),
  transformedRecordkeepers: PropTypes.arrayOf(Types.audienceDetails),
  transformedTpas: PropTypes.arrayOf(Types.audienceDetails),
  transformedConsultants: PropTypes.arrayOf(Types.audienceDetails),
  availableInitialInvestments: PropTypes.arrayOf(
    Types.availableInitialInvestment
  ),
  fetchAvailableInitialInvestments: PropTypes.func.isRequired,
  completeServicesAgreementForm: Types.completeServicesAgreementForm,
  currentUser: Types.user.isRequired,
  submitServicesAgreementForm: PropTypes.func.isRequired,
  isSubmittingServicesAgreementForm: PropTypes.bool.isRequired,
  setShouldTriggerAllServicesAgreementsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllBDPlanAgreementsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllNotificationsPlanAgreementsFetch:
    PropTypes.func.isRequired,
  flashSuccessMessageHandler: PropTypes.func.isRequired,
  addAddendum: PropTypes.string,
  addNotificationsAddendum: PropTypes.string,
  isTaxableAccounts: PropTypes.bool,
  emptyFormRedirectPath: PropTypes.string.isRequired,
  successRedirectPath: PropTypes.string.isRequired,
  setShouldTriggerAllTaxableServicesAgreementsFetch: PropTypes.func.isRequired,
  isBenefitDistribution: PropTypes.bool,
}

const defaultProps = {
  transformedPlanSponsors: null,
  transformedRecordkeepers: null,
  transformedTpas: null,
  transformedConsultants: null,
  availableInitialInvestments: null,
  completeServicesAgreementForm: null,
  addAddendum: null,
  addNotificationsAddendum: null,
  isTaxableAccounts: false,
  isBenefitDistribution: false,
}

function CompletePlanAgreementView({
  flashErrorMessageHandler,
  fetchAvailableInitialInvestments,
  fetchPlanSponsors,
  fetchRecordkeepers,
  fetchTpas,
  fetchConsultants,
  transformedPlanSponsors,
  transformedRecordkeepers,
  transformedTpas,
  transformedConsultants,
  availableInitialInvestments,
  completeServicesAgreementForm,
  currentUser,
  submitServicesAgreementForm,
  isSubmittingServicesAgreementForm,
  setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch,
  setShouldTriggerAllNotificationsPlanAgreementsFetch,
  flashSuccessMessageHandler,
  addAddendum,
  addNotificationsAddendum,
  isTaxableAccounts,
  emptyFormRedirectPath,
  successRedirectPath,
  setShouldTriggerAllTaxableServicesAgreementsFetch,
  isBenefitDistribution,
}) {
  const [error, setError] = useState(false)
  const [isFetchingExistingRecords, setIsFetchingExistingRecords] =
    useState(true)
  const history = useHistory()

  const isPlanFiduciary =
    currentUser.accountAudience === AccountAudience.PLAN_FIDUCIARY
  const allowPlanSponsorSelection =
    completeServicesAgreementForm?.allowPlanSponsorSelection
  const allowTpaConsultantSelection =
    currentUser.accountAudience === AccountAudience.RECORDKEEPER ||
    isPlanFiduciary
  const allowRecordkeeperSelection = computeAllowRecordkeeperSelection(
    isTaxableAccounts,
    currentUser.accountAudience,
    isPlanFiduciary
  )

  useEffect(() => {
    async function getExistingRecords() {
      const promises = []
      if (!isTaxableAccounts) promises.push(fetchAvailableInitialInvestments())
      if (allowPlanSponsorSelection) promises.push(fetchPlanSponsors())
      if (allowRecordkeeperSelection) promises.push(fetchRecordkeepers())
      if (allowTpaConsultantSelection)
        promises.push(fetchTpas(), fetchConsultants())
      setIsFetchingExistingRecords(true)
      try {
        await Promise.all(promises)
      } catch {
        setError(true)
      } finally {
        setIsFetchingExistingRecords(false)
      }
    }
    getExistingRecords()
    return () => history.replace()
  }, [
    allowPlanSponsorSelection,
    allowRecordkeeperSelection,
    allowTpaConsultantSelection,
    isTaxableAccounts,
    fetchAvailableInitialInvestments,
    fetchPlanSponsors,
    fetchTpas,
    fetchConsultants,
    fetchRecordkeepers,
    setError,
    history,
  ])

  if (isEmpty(completeServicesAgreementForm))
    return <Redirect to={emptyFormRedirectPath} />

  const resolvedPlanSponsors = transformedPlanSponsors || []
  const resolvedRecordkeepers = transformedRecordkeepers || []
  const resolvedTpas = transformedTpas || []
  const resolvedConsultants = transformedConsultants || []

  const resolvedInitialInvestments = isTaxableAccounts
    ? [
        {
          templateId: TAXABLE_INITIAL_INVESTMENT_NAME,
          templateName: TAXABLE_INITIAL_INVESTMENT_NAME,
        },
      ]
    : availableInitialInvestments

  const isLoading =
    isFetchingExistingRecords &&
    computeIsLoading({
      transformedPlanSponsors,
      allowPlanSponsorSelection,
      transformedRecordkeepers,
      allowRecordkeeperSelection,
      transformedTpas,
      transformedConsultants,
      allowTpaConsultantSelection,
      availableInitialInvestments: resolvedInitialInvestments,
      isTaxableAccounts,
      error,
    })

  if (isLoading)
    return (
      <div className="complete-services-agreement-view">
        <Spinner />
      </div>
    )
  if (error) return <ComponentFailure />

  const tpasAndConsultants = compact(concat(resolvedTpas, resolvedConsultants))

  const existingPlanSponsorObject = createExistingEntityObject({
    id: completeServicesAgreementForm.planSponsor,
    entities: resolvedPlanSponsors,
  })
  const existingRecordkeeperObject = createExistingEntityObject({
    id: completeServicesAgreementForm.recordkeeper,
    entities: resolvedRecordkeepers,
  })
  const existingTpaConsultantObject = createExistingEntityObject({
    id: completeServicesAgreementForm.tpaConsultant,
    entities: tpasAndConsultants,
  })

  const findAgreementInfo = (agreementTemplateName) =>
    resolvedInitialInvestments.find(
      (investment) => investment.templateName === agreementTemplateName
    )

  const getAgreementType = () => {
    if (currentUser.accountAudience === AccountAudience.RECORDKEEPER) {
      return getRecordkeeperAgreementType(
        currentUser.accountName,
        findAgreementInfo
      )
    }
  }

  const productAgreementType =
    isTaxableAccounts || isBenefitDistribution
      ? undefined
      : ProductAgreementType.ARO
  const taxableInvestment = isTaxableAccounts
    ? findAgreementInfo(TAXABLE_INITIAL_INVESTMENT_NAME)
    : null
  const selectedAgreementType = isTaxableAccounts
    ? taxableInvestment
    : getAgreementType()
  const addendumOption = isTaxableAccounts ? false : addAddendum === 'yes'
  const notificationsAddendumOption = isTaxableAccounts
    ? false
    : addNotificationsAddendum === 'yes'
  const isGrandfatheredUser =
    currentUser?.grandfatheredUser === GrandfatheredUserValue &&
    isGfBDAgreementsEnabled()

  const handleSubmit = async ({
    signingMethod,
    planDetails,
    planSponsor,
    planSponsorDetails,
    recordkeeper,
    recordkeeperDetails,
    tpaConsultant,
    tpaConsultantDetails,
  }) => {
    const planSponsorObject = createAudienceObject({
      entityType: AudienceType.PLAN_SPONSOR,
      entity: planSponsorDetails,
      selectedValue: planSponsor,
    })
    const recordkeeperObject = createAudienceObject({
      entityType: AudienceType.RECORD_KEEPER,
      entity: recordkeeperDetails,
      selectedValue: recordkeeper,
    })
    const tpaConsultantObject = createAudienceObject({
      entityType: tpaConsultantDetails.company.type,
      entity: tpaConsultantDetails,
      selectedValue: tpaConsultant,
    })

    const initialInvestmentOption = find(
      resolvedInitialInvestments,
      (agreement) => agreement.templateId === planDetails.initialInvestment
    )

    const agreementFields = isTaxableAccounts
      ? {}
      : {
          agreementType: selectedAgreementType
            ? selectedAgreementType.templateName
            : initialInvestmentOption?.templateName,
          agreementTemplateID: selectedAgreementType
            ? selectedAgreementType.templateId
            : planDetails.initialInvestment,
        }

    const formData = {
      planName: planDetails.name,
      planNumber: planDetails.planNumber,
      planEIN: planDetails.ein,
      planType: planDetails.type,
      planStatus: planDetails.status,
      planIdNumber: planDetails.providerPlanIdNumber,
      serviceAgreementID: planDetails.serviceAgreementID,
      ...agreementFields,
      requestContactID: currentUser.contactID,
      company: compact([
        planSponsorObject,
        recordkeeperObject,
        tpaConsultantObject,
      ]),
    }

    if (!signingMethod && isPlanFiduciary) {
      signingMethod = signingMethodOptions.find(
        (method) => method.key === SigningMethod.E_SIGN_RSP
      ).value
    }

    const getSubmissiontypeOverride = () => {
      if (isTaxableAccounts) {
        return getTaxableSubmissionType(signingMethod)
      }
      if (isBenefitDistribution && isGrandfatheredUser) {
        return getGFServicesAgreementSubmissionType(signingMethod)
      }
      return undefined
    }
    const submissionTypeOverride = getSubmissiontypeOverride()

    const shouldSubmitGFServicesAgreement =
      isGrandfatheredUser &&
      productAgreementType === ProductAgreementType.ARO &&
      addendumOption
    const submittedAddendumOption = isGrandfatheredUser ? false : addendumOption

    try {
      await submitServicesAgreementForm({
        planAgreementId: planDetails.serviceAgreementID,
        signingMethod,
        submissionType: submissionTypeOverride,
        formData,
        productAgreementType,
        addendumOption: submittedAddendumOption,
        notificationsAddendumOption,
      })
      if (shouldSubmitGFServicesAgreement) {
        await submitServicesAgreementForm({
          planAgreementId: planDetails.serviceAgreementID,
          signingMethod,
          submissionType: getGFServicesAgreementSubmissionType(signingMethod),
          formData,
          productAgreementType,
          addendumOption: false,
          notificationsAddendumOption: false,
        })
      }
      if (isTaxableAccounts) {
        setShouldTriggerAllTaxableServicesAgreementsFetch(true)
      } else if (isBenefitDistribution && isGrandfatheredUser) {
        setShouldTriggerAllBDPlanAgreementsFetch(true)
      } else {
        setShouldTriggerAllServicesAgreementsFetch(true)
        setShouldTriggerAllBDPlanAgreementsFetch(true)
        setShouldTriggerAllNotificationsPlanAgreementsFetch(true)
      }
      history.push({
        pathname: successRedirectPath,
        state: { ignorePrompt: true },
      })
      flashSuccessMessageHandler(
        generateSuccessMessage({
          recordkeeperName: recordkeeperObject?.name,
          signingMethod,
        }),
        { timeout: FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT }
      )
    } catch (e) {
      if (e.status === 504) {
        history.push({
          pathname: successRedirectPath,
          state: { ignorePrompt: true, showTimeoutModal: true },
        })
      } else {
        flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
      }
    }
  }

  const taxableInitialValues =
    isTaxableAccounts && taxableInvestment
      ? { planDetails: { initialInvestment: taxableInvestment.templateId } }
      : {}

  return (
    <>
      {isSubmittingServicesAgreementForm && <FullPageSpinner showBackground />}
      <div className="card full-height complete-services-agreement-view">
        <div className="complete-services-agreement-form-container">
          <div className="form-masthead">
            <h2>Let's capture your services agreement details</h2>
          </div>
          <CompletePlanAgreementForm
            initialValues={mergeAll([
              completeServicesAgreementForm,
              {
                planSponsorDetails: existingPlanSponsorObject,
                recordkeeperDetails: existingRecordkeeperObject,
                tpaConsultantDetails: existingTpaConsultantObject,
              },
              taxableInitialValues,
            ])}
            handleSubmit={handleSubmit}
            planSponsors={allowPlanSponsorSelection ? resolvedPlanSponsors : []}
            recordkeepers={
              allowRecordkeeperSelection ? resolvedRecordkeepers : []
            }
            tpasAndConsultants={
              allowTpaConsultantSelection ? tpasAndConsultants : []
            }
            availableInitialInvestments={resolvedInitialInvestments}
            allowRecordkeeperSelection={allowRecordkeeperSelection}
            allowTpaConsultantSelection={allowTpaConsultantSelection}
            isPlanFiduciary={isPlanFiduciary}
            isTaxableAccounts={isTaxableAccounts}
            currentUser={currentUser}
            addAddendum={addAddendum}
            exitPath={successRedirectPath}
          />
        </div>
      </div>
    </>
  )
}

CompletePlanAgreementView.propTypes = propTypes
CompletePlanAgreementView.defaultProps = defaultProps

export default CompletePlanAgreementView
